import { Stack, Grid, Box } from '@ace-clublabs/shared-component-library';
import { SelectMemberBladeList } from './SelectMemberBlade';
import { MembersDetailCard } from './MembersDetailCard';
import { PhoneType, RoleType } from '@ace-clublabs/common-membership-api-types';
import { useEffect, useState } from 'react';
import { useGlobalState } from '../../state/state';
import { getLoggedInMember } from '../../state/helpers';
import { PrivacyPolicyDisclaimer } from '../Disclaimers';
import { getAddressStr } from '../../utils/stringHelper';

export const MembersDetailsOverview = () => {
  const [boxHeight, setBoxHeight] = useState('auto');

  const updateBoxHeight = () => {
    const membershipSection = document.querySelector('.members-details-overview') as HTMLElement;
    if (membershipSection) {
      setBoxHeight(`${membershipSection.offsetHeight}px`); // Set the blue box background height to the size of the container
    }
  };

  useEffect(() => {
    updateBoxHeight();
    window.addEventListener('resize', updateBoxHeight);
    return () => {
      window.removeEventListener('resize', updateBoxHeight);
    };
  }, []);

  const [membershipDetails] = useGlobalState('memberDetails');
  let { members, loggedInCustomerId } = membershipDetails;
  const loggedInMember = getLoggedInMember(membershipDetails);
  if (!loggedInMember?.rules?.viewMembership)
    members = members?.filter((member) => member?.customerId === loggedInCustomerId);

  const membersItems = members?.map((member, i) => {
    const { firstName, middleName, lastName, nameSuffix, roleType, spinOffStatus } = member;
    const formattedMiddleInitial = middleName ? `${middleName.charAt(0).toUpperCase()} ` : '';
    const suffix = nameSuffix ? `, ${nameSuffix.toUpperCase()}` : '';
    const memberName = `${firstName} ${formattedMiddleInitial}${lastName}${suffix}`;
    return {
      memberName,
      memberRole: roleType,
      customerId: member.customerId,
      dateOfBirth: member.dateOfBirth,
      gender: member.gender,
      email: member.emailAddress,
      phoneNumbers: member.phoneNumbers,
      spinOffStatus,
    };
  });

  const memberHierarchy = [RoleType.PRIMARY, RoleType.ADULT_ASSOCIATE, RoleType.DEPENDENT, RoleType.ASSOCIATE];

  const orderedMemberItems = membersItems?.sort((a, b) => {
    return memberHierarchy.indexOf(a.memberRole) - memberHierarchy.indexOf(b.memberRole);
  });

  const loggedInMemberIndex = orderedMemberItems?.findIndex((mem) => mem.customerId == loggedInCustomerId);

  const [selectedMemberIndex, setSelectedMemberIndex] = useState(loggedInMemberIndex);

  const updateSelectMember = (index: number) => {
    setSelectedMemberIndex(index);
  };
  const selectedMember = orderedMemberItems![selectedMemberIndex ?? 0];

  const loggedInMemberMobileNumberInfo = loggedInMember?.phoneNumbers?.find((phone) => phone.type === PhoneType.MOBILE);
  const loggedInMemberMobileNumber =
    loggedInMemberMobileNumberInfo?.areaCode &&
    loggedInMemberMobileNumberInfo?.prefix &&
    loggedInMemberMobileNumberInfo?.areaCode
      ? `(${loggedInMemberMobileNumberInfo.areaCode}) ${loggedInMemberMobileNumberInfo.prefix}-${loggedInMemberMobileNumberInfo.suffix}`
      : '';

  const loggedInMemberInfo = loggedInMember?.phoneNumbers?.find((phone) => phone.type === PhoneType.HOME);
  const loggedInMemberHomeNumber =
    loggedInMemberInfo?.areaCode && loggedInMemberInfo?.prefix && loggedInMemberInfo?.areaCode
      ? `(${loggedInMemberInfo.areaCode}) ${loggedInMemberInfo.prefix}-${loggedInMemberInfo.suffix}`
      : '';

  const loggedInMemberResidenceAddress = getAddressStr(loggedInMember?.residenceAddress);
  const loggedInMemberMailingAddress = getAddressStr(loggedInMember?.mailingAddress);

  const detailCards = orderedMemberItems?.map((member, i) => {
    return (
      <MembersDetailCard
        clubCode={membershipDetails.clubCode!}
        customerId={member.customerId!}
        name={member.memberName}
        roleType={member.memberRole}
        dateOfBirth={member.dateOfBirth ?? undefined}
        gender={member.gender}
        email={member.email}
        mobile={loggedInMemberMobileNumber}
        home={loggedInMemberHomeNumber !== loggedInMemberMobileNumber ? loggedInMemberHomeNumber : undefined}
        residenceAddress={loggedInMemberResidenceAddress}
        mailingAddress={
          loggedInMemberMailingAddress !== loggedInMemberResidenceAddress ? loggedInMemberMailingAddress : undefined
        }
        isLoggedInMember={member.customerId === loggedInCustomerId}
        className={`card-item-${i}`}
        spinOffStatus={member.spinOffStatus}
      />
    );
  });

  return (
    <Grid
      className="members-details-overview"
      columns={12}
      container
      columnSpacing={'1.5rem'}
      sx={{minHeight: '100vh',
        paddingRight: '0px',
        '@media (max-width: 576px)': {paddingRight: '1rem'}}}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          backgroundColor: '#F7F8FA',
          zIndex: -1,
          width: '100vw',
          height: boxHeight,
        }}
      />
      <Grid
        size={{ md: 5, lg: 5 }}
        visible={{
          xs: false,
          sm: false,
          md: true,
          lg: true,
        }}
      >
        <SelectMemberBladeList
          onSelectClick={updateSelectMember}
          selectedMemberIndex={selectedMemberIndex!}
          orderedMemberItems={orderedMemberItems!}
        />
      </Grid>
      <Grid
        sx={{ marginTop: '3.75rem' }}
        size={{ xs: 12, sm: 12, md: 7, lg: 7 }}
        visible={{
          xs: false,
          sm: false,
          md: true,
          lg: true,
        }}
      >
        <Stack gap={'1rem'}>
          {detailCards?.[selectedMemberIndex || 0]}
          <PrivacyPolicyDisclaimer />
        </Stack>
      </Grid>

      {/** Mobile View - Render Cards Only */}
      <Grid
        sx={{ marginTop: '1.5rem' }}
        size={{ xs: 12, sm: 12, md: 7, lg: 7 }}
        visible={{
          xs: true,
          sm: true,
          md: false,
          lg: false,
        }}
      >
        <Stack gap={'1rem'}>
          {detailCards}

          <PrivacyPolicyDisclaimer />
        </Stack>
      </Grid>
    </Grid>
  );
};
